import React, { useState, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { FaTractor } from 'react-icons/fa';
import { useUser } from '../contexts/UserContext';
import teamService, { UserFarm } from '../services/teamService';
import toast from 'react-hot-toast';
import AccountSelectionModal, { SelectableItem } from './AccountSelectionModal';
import { useTranslation } from 'react-i18next';

interface SubUserFarmSelectorProps {
  selectedFarm: any;
  onFarmSelect: (farm: any) => void;
}

const SubUserFarmSelector: React.FC<SubUserFarmSelectorProps> = ({
  selectedFarm,
  onFarmSelect
}) => {
  const { t } = useTranslation('common');
  const { userFarms } = useUser();
  const [showModal, setShowModal] = useState(false);
  const [farms, setFarms] = useState<UserFarm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFarms();
  }, [userFarms]);

  const fetchFarms = async () => {
    try {
      setLoading(true);
      // Use userFarms from context if available, otherwise fetch
      if (userFarms && userFarms.length > 0) {
        setFarms(userFarms);
      } else {
        const fetchedFarms = await teamService.getMyFarms();
        setFarms(fetchedFarms);
      }
    } catch (error: any) {
      console.error('Error fetching farms:', error);
      toast.error('Failed to load farms');
      setFarms([]);
    } finally {
      setLoading(false);
    }
  };

  const convertToSelectableItems = (): SelectableItem[] => {
    const items: SelectableItem[] = [];

    // Sort farms by name
    const sortedFarms = [...farms].sort((a, b) => 
      a.farmName.localeCompare(b.farmName)
    );

    sortedFarms.forEach(farm => {
      items.push({
        id: farm.farmId,
        name: farm.farmName,
        subtitle: farm.accountName,
        badge: farm.accessType,
        badgeColor: 'blue',
        icon: <FaTractor className="size-4 text-emerald-600" />,
        metadata: farm,
        isActive: true
      });
    });

    return items;
  };

  const handleItemSelect = (item: SelectableItem | null) => {
    if (!item) {
      onFarmSelect(null);
      return;
    }

    if (item.metadata) {
      onFarmSelect(item.metadata);
    }
  };

  const getDisplayText = () => {
    if (!selectedFarm) return t('selection.selectFarm');
    return `🏡 ${selectedFarm.farmName || selectedFarm.accountName}`;
  };

  const getSelectedItem = (): SelectableItem | null => {
    if (!selectedFarm) return null;

    const allItems = convertToSelectableItems();
    return allItems.find(item => item.id === selectedFarm.farmId) || null;
  };

  return (
    <>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {t('selection.farmSelection')}
        </label>
        
        {/* Selected Farm Display - Opens Modal */}
        <button
          onClick={() => setShowModal(true)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white hover:bg-gray-50 flex items-center justify-between group transition-all"
          disabled={loading}
        >
          <span className={selectedFarm ? 'text-gray-900' : 'text-gray-500'}>
            {loading ? t('selection.loadingFarms') : getDisplayText()}
          </span>
          <FiChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
        </button>

        {/* Info Text */}
        {!loading && farms.length === 0 && (
          <p className="text-xs text-gray-500">
            {t('selection.noFarmsAssignedContactOwner')}
          </p>
        )}
      </div>

      {/* Farm Selection Modal */}
      <AccountSelectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleItemSelect}
        items={convertToSelectableItems()}
        selectedItem={getSelectedItem()}
        title={t('selection.selectFarm')}
        searchPlaceholder={t('selection.searchPlaceholderFarmSimple')}
        emptyMessage={t('selection.noFarmsAssignedContactOwner')}
        showClearSelection={true}
        allowGroupExpansion={false}
        showActiveFilter={false}
      />
    </>
  );
};

export default SubUserFarmSelector;
